import { Injectable } from '@angular/core'

import { IProduct } from '../../models/product'
import { UtilityService } from '../utility-service/utility-service.service'
import { ClassificationRulesService } from './classification-rules.service'

@Injectable({
  providedIn: 'root',
})
export class ClassificationRulesFixedIncomeService {
  // Fixed Income
  static isUSFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      !ClassificationRulesFixedIncomeService.isGlobalBond(product) &&
      !ClassificationRulesFixedIncomeService.isRST(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(product.marketOfDomicile, 'UNITED STATES')
    )
  }

  static isGlobalBond(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.assetClassificationLevel1, 'bond') &&
      (UtilityService.wordMatch(product.regionOfInvestment, 'International') ||
        UtilityService.wordMatch(product.regionOfInvestment, 'Global'))
    )
  }

  static isRST(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.investmentStrategy, 'Stable Value') &&
      UtilityService.wordMatch(
        product.productTypeLevel2,
        'Collective Investment Trust (CIT)',
      )
    )
  }

  static isIntlFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      (UtilityService.wordMatch(product.regionOfInvestment, 'International') ||
        UtilityService.wordMatch(product.regionOfInvestment, 'Global'))
    )
  }

  static isDevelopedFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      !this.isEmergingFixedIncome(product)
    )
  }

  static isEmergingFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(
        product.industryCategory,
        'Intermediate Government',
      ) &&
      UtilityService.wordMatch(product.regionOfInvestment, 'International')
    )
  }

  static isGlobalFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.existAnyWordMatch(product.foiTypes, 'global')
    )
  }

  static isFIMultiSector(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(
        product.fixedIncomeStyleBoxQuality,
        'Below Investment Grade',
      ) &&
      UtilityService.wordMatch(
        product.industryCategory,
        'Intermediate-Term Bond',
      )
    )
  }

  static isNonUSFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(product.regionOfInvestment, 'International') &&
      !UtilityService.wordMatch(
        product.industryCategory,
        'Intermediate Government',
      )
    )
  }

  static isIndexFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      (product.managementStrategy === 'Index' ||
        product.managementStrategy === 'Index-Oriented')
    )
  }

  static isActiveFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      product.managementStrategy === 'Active'
    )
  }

  static isLongFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(product.fixedIncomeStyleBoxTerm, 'Long Term')
    )
  }

  static isInterMediateFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      UtilityService.wordMatch(
        product.fixedIncomeStyleBoxTerm,
        'Intermediate Term',
      )
    )
  }

  static isShortFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      (UtilityService.wordMatch(
        product.fixedIncomeStyleBoxTerm,
        'Short Term',
      ) ||
        UtilityService.wordMatch(
          product.fixedIncomeStyleBoxTerm,
          'Limited Term',
        ))
    )
  }

  static isBelowINVGradeFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      !ClassificationRulesFixedIncomeService.isTaxExempt(product) &&
      UtilityService.wordMatch(
        product.fixedIncomeStyleBoxQuality,
        'Below Investment Grade',
      ) &&
      !ClassificationRulesFixedIncomeService.isFIMultiSector(product)
    )
  }

  static isTaxExempt(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      (UtilityService.wordMatch(
        product.fixedIncomeTaxabilityStatus,
        'National Tax Exempt',
      ) ||
        UtilityService.wordMatch(
          product.fixedIncomeTaxabilityStatus,
          'State Tax-Exempt',
        )) &&
      !UtilityService.wordMatch(product.fixedIncomeStyleBoxTerm, 'Money Market')
    )
  }

  static isINVGradeCorpFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      !ClassificationRulesFixedIncomeService.isTaxExempt(product) &&
      UtilityService.wordMatch(
        product.fixedIncomeStyleBoxQuality,
        'Investment Grade',
      )
    )
  }

  static isMuniFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      (UtilityService.wordMatch(
        product.fixedIncomeTaxabilityStatus,
        'National Tax Exempt',
      ) ||
        UtilityService.wordMatch(
          product.fixedIncomeTaxabilityStatus,
          'State Tax-Exempt',
        ))
    )
  }

  // eslint-disable-next-line complexity
  static isTreasuryFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isFixedIncome(product) &&
      !ClassificationRulesFixedIncomeService.isTaxExempt(product) &&
      (UtilityService.wordMatch(
        product.fixedIncomeStyleBoxQuality,
        'Government',
      ) ||
        UtilityService.wordMatch(product.industryCategory, 'Long Government') ||
        UtilityService.wordMatch(
          product.industryCategory,
          'Intermediate Government',
        ))
    )
  }
}
